// « Importer un PDF » on an existing album (owner, 2026-10-17 — #1250): the
// client's finished pages, one 1-up page each, REPLACING the album's pages or
// ADDED after them. Pure. Guard: albumPdfMerge.test.ts
//
// ⚠️ REPLACE NEVER DELETES A PHOTO. The photos the old pages held go back to
// the album's tray (unassigned_photos): a wrong replace is undone in the editor
// by dragging them back, and no client photo is ever lost.

export type ImportMode = 'replace' | 'append';

interface PageLike { page?: number; photos?: unknown; elements?: unknown; [k: string]: unknown }
interface PagesDoc { pages?: PageLike[]; unassigned_photos?: unknown; [k: string]: unknown }

/** The content pages of a session's `pages` (the object form or a bare array). */
export function contentPages(raw: unknown): PageLike[] {
  if (Array.isArray(raw)) return raw as PageLike[];
  const p = (raw as PagesDoc | null)?.pages;
  return Array.isArray(p) ? p : [];
}

/** Every photo id a page shows: its slots, and photo elements of a model page. */
function photosOf(pg: PageLike): string[] {
  const ids = Array.isArray(pg.photos) ? (pg.photos as unknown[]).filter((x): x is string => typeof x === 'string' && !!x) : [];
  const els = Array.isArray(pg.elements) ? (pg.elements as Array<{ type?: unknown; photoId?: unknown }>) : [];
  for (const e of els) if (e && e.type === 'photo' && typeof e.photoId === 'string' && e.photoId) ids.push(e.photoId);
  return ids;
}

export function mergeImportedPages(
  raw: unknown,
  photoIds: string[],
  mode: ImportMode,
): { pages: PagesDoc; pageCount: number } {
  const doc: PagesDoc = Array.isArray(raw) ? { pages: raw as PageLike[] } : { ...((raw as PagesDoc | null) ?? {}) };
  const old = contentPages(raw);
  const tray = Array.isArray(doc.unassigned_photos) ? (doc.unassigned_photos as unknown[]).filter((x): x is string => typeof x === 'string') : [];
  const fresh = (start: number) => photoIds.map((id, i) => ({ page: start + i, layout: '1-up', photos: [id] }));

  if (mode === 'append') {
    const last = old.reduce((m, p) => Math.max(m, Number(p.page) || 0), 0);
    const pages = [...old, ...fresh(last + 1)];
    return { pages: { ...doc, pages, unassigned_photos: tray }, pageCount: pages.length };
  }
  const imported = new Set(photoIds);
  const back = [...tray, ...old.flatMap(photosOf)].filter((id) => !imported.has(id));
  const pages = fresh(1);
  return { pages: { ...doc, pages, unassigned_photos: [...new Set(back)] }, pageCount: pages.length };
}
